<template>
  <div class="platform-view-container">
    <div v-if="loadingData" class="loader-wrapper">
      <div class="loader">
        <div class="loader-icon"></div>
      </div>
    </div>
    <div v-show="!loadingData" class="course-detail position-relative">
      <PlatformBanner
        :banner-url="platform.url_banner"
        :loading="bannerLoading"
        @go-back="returnUrl"
      />
      <div class="platform-content-wrapper">
        <PlatformTabs v-model="activeTab" />
        <PlatformInfoTab
          :active="activeTab === 'info'"
          :platform="platform"
          :status-text="registrationStatusText"
          :status-class="registrationStatusClass"
          :show-access-button="showAccessButton"
          @access="handleAccessPlatform"
        />
        <PlatformCitiesTab
          :active="activeTab === 'ciudades'"
          :data="{ offers: filteredOffers, states: statesWithOffers }"
          :selection="{ selectedOfferId, selectedState }"
          :user-status="{ canPay, alreadyRegistered, isRejected }"
          :loading="loadingOffers"
          @update:selected-state="selectedState = $event"
          @select-offer="selectOffer"
          @postulate="handlePostulate"
          @pay="handlePay"
          @show-breakdown="handleShowBreakdown"
        />
      </div>
      <PlatformRequirementsModal
        v-if="modalPostulationShow"
        ref="postulationModalRef"
        :show="modalPostulationShow"
        :requires="platform.requires"
        :files-user="filesUser"
        :id-type-status="idTypeStatus"
        v-model:url-file="formSave.url_file"
        @close="closePostulationModal"
        @delete-file="handleDeleteFile"
        @upload-files="handleUploadFiles"
        @postulate-with-requires="handlePostulateWithRequires"
      />
      <PlatformPaymentBreakdownModal
        v-if="modalBreakdownShow"
        :show="modalBreakdownShow"
        :offer="breakdownOffer"
        @close="modalBreakdownShow = false"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import PlatformBanner from "@/components/Platform/PlatformBanner.vue";
import PlatformTabs from "@/components/Platform/PlatformTabs.vue";
import PlatformInfoTab from "@/components/Platform/PlatformInfoTab.vue";
import PlatformCitiesTab from "@/components/Platform/PlatformCitiesTab.vue";
import PlatformRequirementsModal from "@/components/Platform/PlatformRequirementsModal.vue";
import PlatformPaymentBreakdownModal from "@/components/Platform/PlatformPaymentBreakdownModal.vue";
import { usePlatformDetails } from "@/composables/usePlatformDetails";
import { useUserRegistration } from "@/composables/useUserRegistration";
import { usePlatformOffers } from "@/composables/usePlatformOffers";
import { usePlatformFiles } from "@/composables/usePlatformFiles";
import { useLocationAPI } from "@/composables/api/useLocationAPI";
import { usePlatformAPI } from "@/composables/api/usePlatformAPI";

// ===== Router & Init =====
const route = useRoute();
const router = useRouter();

// ===== Composables =====
const { platform, offers, loadingData, loadingOffers, loadPlatform } = usePlatformDetails();
const states = ref([]);
const userReg = useUserRegistration(route.params.id);
const {
  idTypeStatus, idTypeStatusPayment, idUserPlatform, alreadyRegistered,
  isRejected, canPay, isFullyEnrolled,
  registrationStatusText, registrationStatusClass,
  loadStatus, postulateMe, handleAccessPlatform,
} = userReg;

const {
  selectedOfferId, selectedState, statesWithOffers, filteredOffers,
  selectOffer,
} = usePlatformOffers(offers, states);

const { filesUser, loadUserFiles, deleteFile, uploadFiles } = usePlatformFiles();

const { getStates } = useLocationAPI();
const { accessPlatform } = usePlatformAPI();

// ===== Local Reactive State =====
const activeTab = ref("info");
const bannerLoading = ref(true);
const modalPostulationShow = ref(false);
const modalBreakdownShow = ref(false);
const breakdownOffer = ref(null);
const formSave = ref({ id_user_platform: "", name: "", url_file: [] });
const postulationModalRef = ref(null);

// ===== Computed =====
const showAccessButton = computed(() => {
  return isFullyEnrolled.value && platform.value.url;
});

// ===== Methods =====
const returnUrl = () => router.go(-1);

const handleShowBreakdown = (offer) => {
  breakdownOffer.value = offer;
  modalBreakdownShow.value = true;
};

const handlePay = () => {
  router.push({ name: 'Postulation', query: { openPayment: '1', id_platform: platform.value.id } });
};

const handlePostulate = async () => {
  const result = await postulateMe(platform.value, selectedOfferId.value, platform.value.autoapproved);
  if (result?.success && result.redirect) {
    router.push({ name: result.redirect });
  }
};

const openPostulationModal = () => {
  modalPostulationShow.value = true;
};

const closePostulationModal = () => {
  modalPostulationShow.value = false;
};

const handleDeleteFile = async (fileId) => {
  await deleteFile(fileId, () => {
    loadUserFiles(idUserPlatform.value, idTypeStatus.value);
  });
};

const handleUploadFiles = async () => {
  const filePond = postulationModalRef.value?.filePond;
  await uploadFiles(filePond, idUserPlatform.value, () => {
    closePostulationModal();
    router.push({ name: "Postulation" });
  });
};

const handlePostulateWithRequires = async () => {
  // First postulate, then open modal for file upload
  const result = await postulateMe(platform.value, selectedOfferId.value, platform.value.autoapproved);
  if (result?.success) {
    openPostulationModal();
  }
};

// ===== Lifecycle =====
onMounted(async () => {
  await loadPlatform(route.params.id);
  await loadStatus();

  const statesData = await getStates();
  states.value = statesData;

  // Load user files if registered
  if (idUserPlatform.value) {
    await loadUserFiles(idUserPlatform.value, idTypeStatus.value);
  }

  // Banner image loading
  if (platform.value.url_banner) {
    const img = new Image();
    img.src = platform.value.url_banner;
    img.onload = () => { bannerLoading.value = false; };
    img.onerror = () => { bannerLoading.value = false; };
  } else {
    bannerLoading.value = false;
  }
});
</script>
<style>
.loader {
  display: flex;
  justify-content: center;
  height: 300px;
}

.loader-icon {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #6d263a;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.datepicker-container {
  width: 100%;
}

input.mx-input {
  border: none;
}

.form-control:hover {
  box-shadow: #b49b57;
  border-color: #b49b57;
}

.filepond--drop-label.filepond--drop-label label {
  text-align: center !important;
  color: black !important;
  font-size: small;
}

.btn-invert {
  border-radius: 30px;
  padding: 0.7rem 2rem;
  border: none;
  background-color: #b49b57;
  color: rgb(53, 16, 32);
}

.btn-invert:hover {
  transition-duration: 0.1s;
  transform: scale(1.05);
  box-shadow: 0 2px 2px 1px #b49b57;
}
</style>
<style scoped>
/* Define la altura máxima y permite el desplazamiento */
.scrollable {
  max-height: 400px;
  overflow-y: auto;
}
.user-header {
  font-weight: bold;
  font-size: 15.6px;
}
.user-row {
  font-size: 15.6px;
}

.start-date,
.course-duration,
.max-places {
  border: 1px solid white;
  border-radius: 1rem;
  padding: 0.3rem 0.7rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.3rem;
}

.start-date > div:first-child,
.start-date > div:nth-child(2) {
  font-size: 1.1rem;
  opacity: 0.8;
}

.course-duration > div:first-child {
  font-size: 1.1rem;
  opacity: 0.8;
}

.max-places > div:first-child {
  font-size: 1.1rem;
  opacity: 0.8;
}

.text-responsive {
  font-size: clamp(0.5rem, 2vw + 0.5rem, 1rem);
}

.text-title-responsive {
  font-size: clamp(0.6rem, 2vw + 0.5rem, 1.3rem);
}

.essential-details {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  margin-top: 1rem;
}

.type_plattform,
.url-platform,
.requirements {
  border: 1px solid white;
  border-radius: 1rem;
  padding: 0.3rem 0.7rem;
  display: flex;
  align-items: center;
  gap: 0.3rem;
}

.first-presale,
.second-presale,
.type-status {
  background: linear-gradient(135deg, #6d263a 60%, #a14e5a 100%);
  border-radius: 1.5rem;
  color: #fff;
  padding: 2rem 1.5rem 1.5rem 1.5rem;
  text-align: center;
  position: relative;
  overflow: hidden;
  min-width: 220px;
  max-width: 100%;
  margin: 0 auto;
}
.presale-title {
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: 0.7rem;
  letter-spacing: 1px;
}
.presale-dates,
.type-status-title {
  font-size: 1.1rem;
  margin-bottom: 1.2rem;
  opacity: 0.85;
}
.presale-price-box,
.sale-price-box,
.current-status-box {
  background: rgba(0, 0, 0, 0.18);
  border-radius: 1rem;
  padding: 0.7rem 0;
  margin-bottom: 1.2rem;
}
.presale-price-box,
.sale-price-box > div:first-child {
  font-size: 1.1rem;
  opacity: 0.8;
}
.presale-price .sale-price .current-status .requirements-title .url-title {
  font-size: 1.7rem;
  font-weight: bold;
  margin-top: 0.2rem;
  letter-spacing: 1px;
}
.presale-city {
  font-size: 1.1rem;
  opacity: 0.85;
}
.general-info-card {
  background: linear-gradient(135deg, #6d263a 60%, #a14e5a 100%);
  border-radius: 1.5rem;
  color: #fff;
  padding: 1.5rem 1.2rem 1.2rem 1.2rem;
  margin-top: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  min-width: 270px;
  position: relative;
  overflow: hidden;
}
.general-info-header .general-info-body {
  font-size: 1.1rem;
}
/* .general-info-details {
  display: flex;
  margin-top: 1rem;
  margin-bottom: 1rem;
  opacity: 0.85;
} */
.apply-btn {
  background: linear-gradient(90deg, #ff9800 60%, #ff5722 100%);
  color: #fff;
  border: none;
  border-radius: 2rem;
  padding: 0.5rem 2.2rem;
  font-weight: bold;
  font-size: 1.1rem;
  box-shadow: 0 2px 8px rgba(255, 152, 0, 0.18);
  cursor: pointer;
  transition: background 0.2s;
}
.apply-btn:hover {
  background: linear-gradient(90deg, #ffb74d 60%, #ff7043 100%);
}
.soccer-ball {
  width: 48px;
  height: 48px;
  margin-left: 1.2rem;
  margin-bottom: -0.5rem;
}

/* Contenedor principal sin padding */
.platform-view-container {
  padding: 0;
  margin: 0;
  width: 100%;
}

.loader-wrapper {
  padding: 2rem;
}

.course-detail {
  background: transparent;
  margin: 0;
  padding: 0;
  border-radius: 0;
}

/* Botón Volver dentro del banner */
.btn-volver {
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 10;
  border-radius: 20px;
}

h1,
h2,
h3,
h4,
h5,
h6,
span {
  color: #fff;
}

.general-info-card.position-relative {
  position: relative;
}
.sale-price-box.position-absolute {
  position: absolute;
  top: 1.2rem;
  right: 1.2rem;
  z-index: 2;
  background: rgba(0, 0, 0, 0.18);
  border-radius: 1rem;
  padding: 0.7rem 1.2rem;
  min-width: 90px;
  text-align: center;
}
.banner-card-header {
  position: relative;
  left: 0;
  top: 0;
  width: 100%;
  padding: 0;
  margin: 0;
  overflow: hidden;
  border-radius: 0;
}
.banner-card-header img {
  width: 100%;
  height: 300px;
  object-fit: cover;
  object-position: top;
  display: block;
}
.btn-banner-top-right {
  position: absolute;
  top: 2rem;
  right: 2rem;
  z-index: 10;
}
.banner-gradient-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  border-radius: 0;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0.3) 100%
  );
  z-index: 2;
}
.description-wrapper {
  position: relative;
  width: 100%;
}
.course-description.collapsed {
  max-height: 122px;
  overflow: hidden;
  position: relative;
  transition: max-height 0.3s;
  margin-bottom: 0;
}
.longe-text {
  display: block;
  color: #615f5f;
  cursor: pointer;
  text-align: right;
  margin-bottom: 0;
}
.requirements-wrapper {
  position: relative;
  width: 100%;
}
.requirements-text.collapsed {
  max-height: 124px;
  overflow: hidden;
  position: relative;
  transition: max-height 0.3s;
  margin-bottom: 0;
}

/* Platform content wrapper con fondo */
.platform-content-wrapper {
  background-image: url('/public/img/background_platform.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;
}

/* Tabs */
.tabs-container {
  display: flex;
  gap: 10px;
}

.tab-btn {
  padding: 8px 24px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  background: transparent;
  color: #fff;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.tab-btn:hover,
.tab-btn.active {
  background: #b49b57;
  border-color: #b49b57;
}

/* Tab Ciudades */
.tab-ciudades {
  min-height: 400px;
}

.section-title {
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

/* Cities Grid */
.cities-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
}

.city-card {
  background: linear-gradient(135deg, #6d263a 60%, #a14e5a 100%);
  border-radius: 1.5rem;
  padding: 1.5rem;
  border: 2px solid transparent;
  cursor: pointer;
  transition: border-color 0.2s ease;
}

.city-card:hover {
  border-color: rgba(180, 155, 87, 0.5);
}

.city-card-selected {
  border-color: #b49b57;
  background: linear-gradient(135deg, #7d3349 60%, #b15e6a 100%);
}

/* Botón Golden */
.btn-golden {
  background-color: #b49b57;
  border-color: #b49b57;
  border-radius: 20px;
  padding: 8px 24px;
  font-weight: 600;
  color: #1a1a1a;
  transition: all 0.3s ease;
}

.btn-golden:hover {
  background-color: #d1b25d;
  border-color: #d1b25d;
  color: #1a1a1a;
}

.btn-golden:disabled {
  background-color: #b7b7b7;
  border-color: #b7b7b7;
  color: #666;
  cursor: not-allowed;
  opacity: 0.7;
}

/* Botón Golden con animación hover elegante */
.btn-golden-hover {
  position: relative;
  display: flex;
  align-items: center;
  background-color: #b49b57;
  border: none;
  border-radius: 50px;
  padding: 6px;
  font-weight: 600;
  color: #1a1a1a;
  overflow: hidden;
  min-width: 180px;
  height: 48px;
  transition: background-color 0.3s ease;
}

.btn-golden-hover:hover:not(:disabled) {
  background-color: #d1b25d;
}

.btn-golden-hover:disabled {
  background-color: #b7b7b7;
  color: #666;
  cursor: not-allowed;
  opacity: 0.7;
}

.btn-golden-hover .btn-icon-circle {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background-color: #fff;
  color: #1a1a1a;
  border-radius: 50%;
  font-size: 18px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  right: 6px;
  transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1), right 0.5s cubic-bezier(0.4, 0, 0.2, 1), left 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-golden-hover:hover:not(:disabled) .btn-icon-circle {
  right: auto;
  left: 6px;
}

.btn-golden-hover .btn-text-label {
  white-space: nowrap;
  color: #1a1a1a;
  margin-left: 12px;
  margin-right: 48px;
  transition: margin 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-golden-hover:hover:not(:disabled) .btn-text-label {
  margin-left: 48px;
  margin-right: 12px;
}

.btn-golden-hover .icon-initial,
.btn-golden-hover .icon-hover {
  position: absolute;
  transition: opacity 0.4s ease, transform 0.4s ease;
}

.btn-golden-hover .icon-initial {
  opacity: 1;
  transform: rotate(0deg);
}

.btn-golden-hover .icon-hover {
  opacity: 0;
  transform: rotate(-90deg);
}

.btn-golden-hover:hover:not(:disabled) .icon-initial {
  opacity: 0;
  transform: rotate(90deg);
}

.btn-golden-hover:hover:not(:disabled) .icon-hover {
  opacity: 1;
  transform: rotate(0deg);
}

.btn-golden-hover:disabled .btn-icon-circle {
  background-color: #e0e0e0;
  color: #999;
}

.city-name {
  font-size: 1.5rem;
  font-weight: 700;
  color: #fff;
  text-align: center;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
}

.city-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.city-detail-item {
  background: rgba(0, 0, 0, 0.15);
  border-radius: 12px;
  padding: 8px 16px;
  color: #fff;
  font-size: 0.9rem;
}

.city-detail-item span {
  color: #fff;
}

/* Loader icon para tab ciudades */
.loader-icon.mx-auto {
  margin: 0 auto;
}

/* Tab Info Styles */
.tab-info {
  color: #fff;
}

.platform-title {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 1rem;
}

.platform-description {
  font-size: 0.95rem;
  line-height: 1.6;
  opacity: 0.9;
}

.info-input {
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  padding: 12px 16px;
  color: #fff;
  font-size: 0.9rem;
  min-height: 48px;
  display: flex;
  align-items: center;
}

.info-input-multiline {
  flex-direction: column;
  align-items: flex-start;
  min-height: auto;
}

.info-label {
  color: #fff;
  font-size: 0.9rem;
}

.video-container {
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  overflow: hidden;
  aspect-ratio: 16/9;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

.video-player {
  width: 100%;
  height: 100%;
}

.video-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.2);
}

.video-icon {
  font-size: 64px;
  color: rgba(255, 255, 255, 0.5);
}

@media (max-width: 768px) {
  .cities-grid {
    grid-template-columns: 1fr;
  }
  
  .tab-btn {
    padding: 6px 16px;
    font-size: 0.75rem;
  }

  .platform-title {
    font-size: 1.5rem;
  }

  .video-container {
    margin-top: 1rem;
  }
}




.btn-fvf-access {
  background: linear-gradient(135deg, #500e22 0%, #8D1B3D 50%, #500e22 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  position: relative;
  transition: all 0.3s ease;
  overflow: hidden;
  text-transform: uppercase;
  font-weight: bold;
  letter-spacing: 1px;
  z-index: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 4px 15px rgba(112, 19, 47, 0.4);
}

/* Lava Background Effect */
.btn-fvf-access::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: 
    radial-gradient(circle at 50% 50%, rgba(197, 160, 89, 0.2) 0%, transparent 20%),
    radial-gradient(circle at 10% 20%, rgba(197, 160, 89, 0.15) 0%, transparent 20%),
    radial-gradient(circle at 90% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 15%);
  background-size: 50% 50%, 60% 60%, 50% 50%;
  animation: lava-flow 10s ease-in-out infinite alternate;
  z-index: -1;
  opacity: 0;
  transition: opacity 0.5s ease;
}

.btn-fvf-access:hover::before {
  opacity: 1;
}

@keyframes lava-flow {
  0% { transform: rotate(0deg) translate(0, 0); }
  33% { transform: rotate(5deg) translate(10px, 10px); }
  66% { transform: rotate(-5deg) translate(-10px, 5px); }
  100% { transform: rotate(0deg) translate(0, 0); }
}

.btn-fvf-access:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(197, 160, 89, 0.4);
  color: #fff;
}

.btn-fvf-access:active {
  transform: translateY(1px);
}

/* Span Animations for the Border Snake Effect - SLOWER (2s cycle) */
.btn-fvf-access span:nth-child(1),
.btn-fvf-access span:nth-child(2),
.btn-fvf-access span:nth-child(3),
.btn-fvf-access span:nth-child(4) {
  position: absolute;
  display: block;
}

/* Top Border */
.btn-fvf-access span:nth-child(1) {
  top: 0;
  left: -100%;
  width: 100%;
  height: 3px;
  background: linear-gradient(90deg, transparent, #C5A059);
  animation: btn-anim1 2s linear infinite;
}
@keyframes btn-anim1 {
  0% { left: -100%; }
  50%, 100% { left: 100%; }
}

/* Right Border */
.btn-fvf-access span:nth-child(2) {
  top: -100%;
  right: 0;
  width: 3px;
  height: 100%;
  background: linear-gradient(180deg, transparent, #C5A059);
  animation: btn-anim2 2s linear infinite;
  animation-delay: 0.5s;
}
@keyframes btn-anim2 {
  0% { top: -100%; }
  50%, 100% { top: 100%; }
}

/* Bottom Border */
.btn-fvf-access span:nth-child(3) {
  bottom: 0;
  right: -100%;
  width: 100%;
  height: 3px;
  background: linear-gradient(270deg, transparent, #C5A059);
  animation: btn-anim3 2s linear infinite;
  animation-delay: 1s;
}
@keyframes btn-anim3 {
  0% { right: -100%; }
  50%, 100% { right: 100%; }
}

/* Left Border */
.btn-fvf-access span:nth-child(4) {
  bottom: -100%;
  left: 0;
  width: 3px;
  height: 100%;
  background: linear-gradient(360deg, transparent, #C5A059);
  animation: btn-anim4 2s linear infinite;
  animation-delay: 1.5s;
}
@keyframes btn-anim4 {
  0% { bottom: -100%; }
  50%, 100% { bottom: 100%; }
}



</style>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
